/**
 * Currency and capacity icons.
 *
 * User: "add icons, coins, WAX, and slots" (2026-09-02), then "generate
 * normally with higgsfield icons for energy, in-game currency. Let it be in
 * game style" (2026-09-03).
 *
 * Every number in the economy used to be a bare numeral next to a word —
 * `4600 WAX`, `250 Ember`, `8/25 SLOTS`. Fine to read, nothing to recognise:
 * prices, balances and rewards all looked like the same grey text, and the
 * one thing a player checks constantly (can I afford this?) had no shape to
 * find.
 *
 * ## These are renders now, not drawings
 *
 * The first pass authored them as inline SVG, on the argument that a currency
 * is a symbol rather than a thing in the world and a two-tone shape survives
 * 18px better than a downscaled render. Half of that was right and half was
 * not. They did survive 18px — but they were the only pictures in the game
 * that were *drawn*, so the wallet in the header sat in a different visual
 * language from the loot in the stash, the plates on the action bar and the
 * props in the dungeon, all of which are renders.
 *
 * So they are renders now, generated in the same faceted low-poly language as
 * `/assets/ui/items` and keyed onto transparency. Each one was proofed at 24,
 * 44 and 128 px against the plate colour they actually sit on before shipping;
 * the first ember came back with a bloom halo around it and was regenerated
 * rather than accepted, because a soft glow is the one finish this style does
 * not have.
 *
 * ## What each object is, and why
 *
 * A **coin** for Wax, struck with a flame — the currency of a game called
 * Tallow should not be a generic gold disc. A **violet crystal with a burning
 * core** for Ember: it is the currency you cannot earn by delving, and
 * something burning the wrong colour says "unnatural, precious" better than a
 * second gold coin would. A **chest** for Slots, in plain wood and iron
 * rather than gold, because capacity is not money and must not look like it.
 * A **candle** for Energy — deliberately not the bolt `hud-theme.ts` already
 * uses for the in-combat resource, since two unrelated things share the word
 * "energy" in this game and the least this can do is not share the picture.
 * A **spark** for XP, which is not a coin shape because XP cannot be spent.
 *
 * Sizing stays in `em` so an icon tracks whatever text it sits beside, which
 * is why callers can scale one by wrapping it in a font-size and asking for
 * `1em` (see `shop.ts`).
 */

import { t, N_ } from '../i18n/i18n';

export type CurrencyKind = 'wax' | 'ember' | 'slots' | 'xp' | 'energy';

const ICON_BASE = '/assets/ui/currency';

/** The size the files are actually authored at. */
const ICON_PX = 256;

const LABELS: Record<CurrencyKind, string> = {
  wax: N_('Wax'), ember: N_('Ember'), slots: N_('Slots'), xp: N_('XP'), energy: N_('Energy'),
};

/**
 * One icon, sized in `em` so it matches the text it sits in without a
 * per-call size class.
 *
 * `aria-hidden` with an empty `alt` because every caller writes the amount
 * and the currency name next to it — a screen reader announcing "image, wax"
 * before "4600 Wax" is noise, not information.
 *
 * `loading="eager"`: these sit in the header and in every price, so the lazy
 * path would only add a flash of nothing to the first thing a player reads.
 */
export function currencyIcon(kind: CurrencyKind, emSize = 1.1): string {
  return `<img src="${ICON_BASE}/${kind}.webp" alt="" aria-hidden="true" draggable="false"`
    // The attributes carry the file's real pixel size so the browser can
    // reserve the right box before it decodes; the inline style is what
    // actually sizes it, in `em`, which an HTML attribute cannot express.
    + ` width="${ICON_PX}" height="${ICON_PX}" loading="eager" decoding="async"`
    + ` style="width:${emSize}em;height:${emSize}em"`
    + ` class="inline-block shrink-0 object-contain align-[-0.15em] select-none" />`;
}

/**
 * `<icon> 4600 Wax` as one non-wrapping unit.
 *
 * The word stays next to the glyph rather than being replaced by it. A new
 * player has no idea what a violet flame means yet, and a price that reads
 * only as a symbol is a price they have to learn before they can shop. Once
 * the glyph is familiar it is what they actually scan for, and the word costs
 * nothing to keep.
 */
export function amountMarkup(kind: CurrencyKind, amount: number | string, opts: {
  /** Prefix like `+` for a reward, or `Sell — ` for a price. */
  prefix?: string;
  /** Drop the trailing word — for tight rows where the glyph alone must do. */
  hideLabel?: boolean;
  /** Tailwind colour class for the numeral. */
  tone?: string;
} = {}): string {
  const tone = opts.tone ?? defaultTone(kind);
  // The space before the label is a real text node, not decoration. Flex
  // ignores whitespace-only children for layout (the gap does the spacing),
  // but `textContent` keeps it — so this reads as "4600 Wax" to a screen
  // reader and to anyone copying the number out, instead of "4600Wax".
  const label = opts.hideLabel ? '' : ` <span class="text-text-dim">${t(LABELS[kind])}</span>`;
  const prefix = opts.prefix ?? '';
  return `<span class="inline-flex items-center gap-1 whitespace-nowrap">`
    + currencyIcon(kind)
    + `<span class="tabular-nums ${tone}">${prefix}${amount}</span>`
    + label
    + '</span>';
}

/** The colour each kind wears everywhere, so one currency never has two looks. */
export function defaultTone(kind: CurrencyKind): string {
  switch (kind) {
    case 'wax': return 'text-gold';
    case 'ember': return 'text-purple-light';
    case 'xp': return 'text-gold-bright';
    case 'energy': return 'text-gold-bright';
    default: return 'text-text';
  }
}

/** `<icon> 8/25 Slots` — used against capacity, which is a ratio and not an amount. */
export function slotsMarkup(used: number, capacity: number, opts: { hideLabel?: boolean } = {}): string {
  return amountMarkup('slots', `${used}/${capacity}`, { hideLabel: opts.hideLabel });
}

/** Plain-text fallback for the few places that set `textContent` rather than `innerHTML`. */
export function amountText(kind: CurrencyKind, amount: number | string, prefix = ''): string {
  return `${prefix}${amount} ${t(LABELS[kind])}`;
}
